import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { LOGO_URL } from "@/lib/brand";
import { useSales } from "@/contexts/SalesContext";
import api from "@/lib/api";
import HeroVideoStage from "@/components/landing/HeroVideoStage";

const NAV_ITEMS = [
  { href: "#produto",     label: "Produto" },
  { href: "#fluxo",       label: "Como funciona" },
  { href: "#conformidade", label: "Conformidade" },
  { href: "#planos",      label: "Planos" },
];

const FEATURES = [
  { i: "fa-link",          t: "Link anônimo por avaliação",
    d: "Cada GHE/setor recebe um link único. Colaboradores respondem sem login, em qualquer dispositivo. Anonimato + LGPD nativo." },
  { i: "fa-chart-column",  t: "Dashboard executivo",
    d: "Risco médio, matriz AIHA, mapa de calor por dimensão e evolução longitudinal. Tudo recalculado em tempo real." },
  { i: "fa-camera-retro",  t: "OCR de formulários impressos",
    d: "Importe questionários preenchidos à mão via foto ou PDF. IA classifica as respostas para revisão e consolida na base." },
  { i: "fa-file-pdf",      t: "Relatório AEP auditável",
    d: "Capa branded, sumário, matriz, plano de ação, checklist MTE, termo LGPD e assinaturas — pronto para PGR/PCMSO." },
  { i: "fa-users-gear",    t: "Multi-empresa & RBAC",
    d: "Admin · Gestor · Cliente. Convide por e-mail, controle acessos por empresa, audite cada operação." },
  { i: "fa-shield-halved", t: "LGPD-by-design",
    d: "Consentimento explícito, hash de IP, retenção configurável e auditoria imutável de toda operação sensível." },
];

const FLOW_STEPS = [
  { n: "01", t: "Cadastre a empresa", d: "Razão social, CNPJ, CNAE, endereço e logo. ViaCEP auto-completa o endereço." },
  { n: "02", t: "Crie a avaliação",   d: "Defina GHE, função e responsável técnico. Gere o link público anônimo." },
  { n: "03", t: "Distribua aos colaboradores", d: "Envie o link por e-mail, QR Code ou compartilhe pelo WhatsApp interno." },
  { n: "04", t: "Monitore em tempo real", d: "Acompanhe respostas chegando, qualidade dos dados (linha-reta, completude) e risco emergente." },
  { n: "05", t: "Construa o plano de ação", d: "Sugestões automáticas por dimensão. Edite responsável, prazo, prioridade e status." },
  { n: "06", t: "Gere o AEP", d: "Documento técnico consolidado com capa, sumário, matriz AIHA, conclusão e assinaturas." },
];

const KPI_HEADLINE = [
  { v: "54",   l: "perguntas validadas" },
  { v: "9",    l: "dimensões psicossociais" },
  { v: "AIHA", l: "matriz de risco" },
  { v: "PGR",  l: "compatibilidade NR-01" },
];

const COMPLIANCE = [
  { t: "NR-01",  d: "Gerenciamento de riscos ocupacionais — fatores psicossociais inseridos no inventário de riscos." },
  { t: "NR-17",  d: "Ergonomia e organização do trabalho — análise ergonômica preliminar." },
  { t: "LGPD",   d: "Lei 13.709/2018 — consentimento, retenção e direito de acesso/exclusão." },
  { t: "ISO 45003", d: "Saúde e segurança ocupacional psicológica — diretrizes adotadas pelo questionário." },
];

export default function Landing() {
  const { whatsappUrl, mailtoUrl } = useSales();
  const [paidPlans, setPaidPlans] = useState([]);

  useEffect(() => {
    api.get("/pricing/plans")
      .then((r) => setPaidPlans(r.data.plans || []))
      .catch(() => setPaidPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* ===================== NAV ===================== */}
      <header className="border-b border-slate-200 bg-white/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-11 w-auto" />
            <div className="hidden sm:block border-l border-slate-200 pl-3">
              <div className="overline text-emerald-700 text-[10px]">NR-01 · SST</div>
              <div className="text-xs text-slate-500">Plataforma Psicossocial</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((n) => (
              <a key={n.href} href={n.href}
                 className="px-3 py-2 text-sm text-slate-600 hover:text-emerald-900 transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="header-login-link" className="btn btn-ghost text-sm">Entrar</Link>
            <Link to="/register" data-testid="header-register-link" className="btn btn-primary text-sm">
              Testar grátis
            </Link>
          </div>
        </div>
      </header>

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        {/* layered backdrop */}
        <div className="absolute inset-0 grid-bg opacity-60"></div>
        <div className="absolute -top-32 -right-32 w-[640px] h-[640px] bg-emerald-200/40 rounded-full blur-3xl"></div>
        <div className="absolute top-40 left-[-200px] w-[480px] h-[480px] bg-[#8BC53F]/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 text-[11px] font-bold uppercase tracking-widest mb-8">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              NR-01 · vigência 2025/2026 · auditoria MTE
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Riscos psicossociais,<br/>
              <span className="text-emerald-900">consolidados em uma plataforma</span>
              <span className="text-[#8BC53F]">.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Do questionário validado ao <strong className="text-slate-900">relatório AEP imprimível</strong>:
              link anônimo para colaboradores, dashboard em tempo real, matriz AIHA, OCR de formulários
              digitalizados e plano de ação cronológico — tudo em conformidade com NR-01 e LGPD.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="btn btn-primary text-base group">
                <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i> Testar grátis
              </Link>
              <a href={whatsappUrl()} target="_blank" rel="noreferrer"
                 data-testid="hero-cta-sales" className="btn btn-secondary text-base">
                <i className="fa-brands fa-whatsapp text-emerald-700"></i> Falar com comercial
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl border-t border-slate-200 pt-8">
              {KPI_HEADLINE.map((k) => (
                <div key={k.l}>
                  <div className="kpi-number text-3xl text-emerald-900">{k.v}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{k.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero artwork: cinematic 6-scene loop (Demo Reel) */}
          <div className="lg:col-span-5">
            <HeroVideoStage />
          </div>
        </div>
      </section>

      {/* ===================== TRUST BAR ===================== */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-4 text-[11px] uppercase tracking-widest text-slate-500">
          <span>Compatível com</span>
          <span className="font-bold text-slate-700">NR-01 · 2025</span>
          <span className="font-bold text-slate-700">NR-17 · ergonomia</span>
          <span className="font-bold text-slate-700">ISO 45003</span>
          <span className="font-bold text-slate-700">LGPD · art. 18</span>
          <span>Sollute Ocupacional</span>
        </div>
      </section>

      {/* ===================== PRODUCT ===================== */}
      <section id="produto" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Seis recursos centrais</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Um pipeline contínuo —<br/>
              <span className="text-emerald-900">do questionário ao laudo técnico.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base text-slate-600 leading-relaxed">
              Pensado por engenheiros de segurança e ergonomistas. Cada módulo cobre um passo
              real do gerenciamento de riscos psicossociais sem fricção, sem planilha.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <article key={f.t} className="group relative card card-hover border-slate-200">
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-emerald-500/0 to-emerald-500/10 rounded-bl-3xl"></div>
              <div className="w-11 h-11 bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4 group-hover:bg-emerald-900 group-hover:border-emerald-900 transition-colors">
                <i className={`fa-solid ${f.i} text-emerald-900 group-hover:text-white text-lg`}></i>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{f.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ===================== FLOW ===================== */}
      <section id="fluxo" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="overline mb-4">Como funciona</div>
          <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl">
            Seis passos — de hoje até o relatório de auditoria.
          </h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
            {FLOW_STEPS.map((s) => (
              <div key={s.n} className="bg-white p-7 hover:bg-emerald-50/50 transition-colors">
                <div className="font-mono text-3xl text-[#8BC53F] font-bold mb-3">{s.n}</div>
                <div className="font-display text-lg font-bold mb-2">{s.t}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== COMPLIANCE ===================== */}
      <section id="conformidade" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Conformidade técnica</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Construído sobre normas vigentes — não sobre achismo.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Cada decisão metodológica do Sollute NR-01 está documentada com a referência normativa
              correspondente. O documento AEP gerado já vem com o checklist de auditoria do MTE pronto.
            </p>
            <a href={mailtoUrl("Documentação técnica · Sollute NR-01")}
               className="btn btn-secondary mt-8" data-testid="compliance-cta">
              <i className="fa-solid fa-file-lines"></i> Solicitar documentação técnica
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {COMPLIANCE.map((c) => (
                <div key={c.t} className="bg-white p-6">
                  <div className="font-mono font-bold text-emerald-900 text-sm mb-2">{c.t}</div>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PLANS ===================== */}
      <section id="planos" className="bg-emerald-950 text-white relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#8BC53F]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-emerald-700/30 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="overline text-emerald-400 mb-4">Planos</div>
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">
                Comece grátis. Escolha o plano pelo número de vidas.
              </h2>
            </div>
            <div className="lg:col-span-5 text-emerald-200/80 text-sm leading-relaxed">
              Todos os planos pagos liberam <strong className="text-white">geração ilimitada do relatório AEP em PDF</strong>,
              empresas e avaliações ilimitadas. <strong className="text-white">Plano anual com pagamento em até 12x sem juros no cartão</strong> —
              acesso liberado por 12 meses; após esse período renove para manter ativo.
            </div>
          </div>

          {/* Paid tier grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="landing-paid-plans">
            {paidPlans.map((p, idx) => {
              const annualTotal = p.amount * 12;
              const installment = annualTotal / 12;
              return (
              <div key={p.id}
                   className={`p-6 border ${idx === 1
                     ? "bg-gradient-to-br from-emerald-900 to-emerald-800 border-[#8BC53F]/60 shadow-2xl relative"
                     : "bg-white/5 border-emerald-300/20 backdrop-blur-sm"}`}
                   data-testid={`landing-plan-${p.id}`}>
                {idx === 1 && (
                  <div className="absolute -top-3 right-4 bg-[#8BC53F] text-emerald-950 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                    Mais popular
                  </div>
                )}
                <div className="overline text-[#8BC53F] mb-2 text-[9px]">{p.label}</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-[10px] text-emerald-200/70 uppercase tracking-wider">12x de</span>
                  <span className="font-display text-3xl font-extrabold text-white">
                    R$ {installment.toFixed(2).replace(".", ",")}
                  </span>
                </div>
                <div className="text-[10px] text-[#8BC53F] uppercase tracking-wider font-bold mb-1">sem juros</div>
                <div className="text-[10px] text-emerald-200/70 mb-4">
                  Total R$ {annualTotal.toFixed(2).replace(".", ",")} · 12 meses
                </div>
                <ul className="space-y-1.5 text-xs text-emerald-50/90 mb-5">
                  <li><i className="fa-solid fa-check text-[#8BC53F] mr-1"></i> Até {p.max_lives} colaboradores</li>
                  <li><i className="fa-solid fa-check text-[#8BC53F] mr-1"></i> AEP em PDF ilimitado</li>
                  <li><i className="fa-solid fa-check text-[#8BC53F] mr-1"></i> Multi-empresa + RBAC</li>
                </ul>
                <Link to="/register"
                      data-testid={`landing-plan-cta-${p.id}`}
                      className={`btn w-full justify-center text-xs ${idx === 1
                        ? "bg-[#8BC53F] text-emerald-950 hover:bg-white"
                        : "bg-white text-emerald-950 hover:bg-emerald-50"}`}>
                  <i className="fa-solid fa-arrow-right"></i> Começar
                </Link>
              </div>
            );})}
          </div>

          {/* Trial + Enterprise framing cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Trial */}
            <div className="bg-white/5 border border-emerald-300/20 p-8 backdrop-blur-sm" data-testid="plan-trial">
              <div className="overline text-emerald-300/80 mb-3">Demonstração</div>
              <div className="font-display text-3xl font-extrabold mb-1">Grátis</div>
              <p className="text-emerald-100/70 text-sm mb-6">
                Use a plataforma inteira por tempo indefinido. Quando precisar gerar o relatório AEP em PDF,
                escolha um dos planos pagos acima — ativação imediata.
              </p>
              <ul className="space-y-2 text-sm text-emerald-50/90 mb-8">
                {[
                  ["fa-check", "Empresas, avaliações e link público"],
                  ["fa-check", "Dashboard, mapa de calor, comparação longitudinal"],
                  ["fa-check", "Plano de ação editável"],
                  ["fa-check", "OCR de formulários impressos"],
                  ["fa-xmark", "Geração de PDF / relatório AEP", "off"],
                ].map(([icon, label, off]) => (
                  <li key={label} className={`flex items-start gap-2 ${off ? "text-emerald-100/50 line-through" : ""}`}>
                    <i className={`fa-solid ${icon} mt-1 ${off ? "text-rose-300/70" : "text-[#8BC53F]"}`}></i>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" data-testid="plan-trial-cta"
                    className="btn bg-white text-emerald-950 hover:bg-emerald-50 w-full justify-center">
                Testar grátis
              </Link>
            </div>

            {/* Enterprise */}
            <div className="bg-gradient-to-br from-emerald-900 to-emerald-800 border border-[#8BC53F]/60 p-8 relative shadow-2xl" data-testid="plan-full">
              <div className="absolute -top-3 right-6 bg-[#8BC53F] text-emerald-950 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                Acima de 250 vidas
              </div>
              <div className="overline text-[#8BC53F] mb-3">Enterprise</div>
              <div className="font-display text-3xl font-extrabold mb-1">Sob consulta</div>
              <p className="text-emerald-100/80 text-sm mb-6">
                Para consultorias SST, departamentos de saúde ocupacional e empresas com força de
                trabalho acima de 250 colaboradores. Atendimento dedicado.
              </p>
              <ul className="space-y-2 text-sm text-white mb-8">
                {[
                  "Tudo dos planos pagos, sem limites",
                  "Múltiplos avaliadores com RBAC granular",
                  "Acordo comercial customizado",
                  "Suporte técnico dedicado",
                ].map((label) => (
                  <li key={label} className="flex items-start gap-2">
                    <i className="fa-solid fa-check mt-1 text-[#8BC53F]"></i>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-2">
                <a href={whatsappUrl("Olá! Tenho mais de 250 colaboradores e quero conhecer o plano Enterprise do Sollute NR-01.")}
                   target="_blank" rel="noreferrer"
                   data-testid="plan-full-cta-whatsapp"
                   className="btn bg-[#8BC53F] text-emerald-950 hover:bg-white flex-1 justify-center">
                  <i className="fa-brands fa-whatsapp"></i> WhatsApp comercial
                </a>
                <a href={mailtoUrl()} data-testid="plan-full-cta-email"
                   className="btn bg-white/10 border border-white/30 text-white hover:bg-white/20 flex-1 justify-center">
                  <i className="fa-solid fa-envelope"></i> Enviar e-mail
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-12 w-auto mb-4" />
            <p className="text-xs text-slate-500 leading-relaxed">
              Plataforma de gestão de riscos psicossociais conforme a NR-01.
              Desenvolvido por Sollute Ocupacional.
            </p>
          </div>
          <div className="text-xs text-slate-600">
            <div className="overline mb-3">Produto</div>
            <ul className="space-y-2">
              <li><a href="#produto" className="hover:text-emerald-900">Recursos</a></li>
              <li><a href="#fluxo" className="hover:text-emerald-900">Como funciona</a></li>
              <li><a href="#conformidade" className="hover:text-emerald-900">Conformidade</a></li>
              <li><a href="#planos" className="hover:text-emerald-900">Planos</a></li>
              <li><Link to="/login" className="hover:text-emerald-900">Entrar</Link></li>
            </ul>
          </div>
          <div className="text-xs text-slate-600">
            <div className="overline mb-3">Comercial</div>
            <ul className="space-y-2">
              <li><a href={whatsappUrl()} target="_blank" rel="noreferrer" className="hover:text-emerald-900"><i className="fa-brands fa-whatsapp mr-1"></i> Falar com comercial</a></li>
              <li><a href={mailtoUrl()} className="hover:text-emerald-900"><i className="fa-solid fa-envelope mr-1"></i> Enviar e-mail</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-slate-500 flex flex-wrap justify-between gap-3">
            <div>© 2026 Sollute Ocupacional · NR-01 Platform</div>
            <div>NR-01 · NR-17 · ISO 45003 · LGPD</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
